import React, { useState, useEffect } from "react";
import { storage, firestore, auth, functions,analytics } from "../firebaseConfig";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { collection, addDoc, doc, updateDoc, query, where, getDocs,getDoc } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import { v4 as uuidv4 } from "uuid";
import { logEvent } from "firebase/analytics";

const allowedFileTypes = ["application/pdf", "image/png", "image/jpeg", "text/plain", "text/csv", "application/json", "text/html", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];

// Function to convert camelCase to Proper English
const toProperEnglish = (str) => {
  return str
    .replace(/([A-Z])/g, " $1") // Add space before capital letters
    .replace(/^./, (match) => match.toUpperCase()) // Capitalize first letter
    .trim();
};

// Function to convert Proper English to camelCase
const toCamelCase = (str) => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => (index === 0 ? word.toLowerCase() : word.toUpperCase()))
    .replace(/\s+/g, "");
};

const UploadContract = () => {
  const [user] = useAuthState(auth);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [contractId, setContractId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [editedFields, setEditedFields] = useState({});

  // Connect to Firebase Emulator in Development Mode
  if (process.env.NODE_ENV === "development") {
    connectFunctionsEmulator(functions, "localhost", 5001);
  }

  // Fetch the user's organization
  useEffect(() => {
    logEvent(analytics, 'upload_page');
    if (user) {
      const fetchOrg = async () => {
        const userQuery = doc(firestore, "users", user.uid)
        const userSnapshot = await getDoc(userQuery);
        // console.log(userSnapshot)
        if (userSnapshot.exists()) {
          const userData = userSnapshot.data();
          setOrgId(userData.orgId);
        }
      };
      fetchOrg();
    }
  }, [user]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!allowedFileTypes.includes(selectedFile.type)) {
      alert("Invalid file type. Allowed formats: PDF, PNG, JPG, TXT, CSV, JSON, HTML, DOCX.");
      return;
    }
    setFile(selectedFile);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.files.length > 0) {
      const droppedFile = event.dataTransfer.files[0];
      if (!allowedFileTypes.includes(droppedFile.type)) {
        alert("Invalid file type.");
        return;
      }
      setFile(droppedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || !orgId) return;
    setUploading(true);

    // const newContractId = uuidv4(); // Generate UUID for contract
    // setContractId(newContractId);
    
    const contractRef = await addDoc(collection(firestore, "contracts"), {
      id: '', // Store the UUID as a field
      orgId: orgId,
      fileName: file.name,
      fileUrl:'',
      uploadedBy: user.uid,
      status: "processing",
      createdAt: new Date(),
    });

    const storageRef = ref(storage, `organizations/${orgId}/contracts/${contractRef.id}/${file.name}`);

    try {
      // Store contract details in Firestore with the generated UUID
      // const contractRef = await addDoc(collection(firestore, "contracts"), {
      //   id: newContractId, // Store the UUID as a field
      //   orgId: orgId,
      //   fileName: file.name,
      //   fileUrl,
      //   uploadedBy: user.uid,
      //   status: "processing",
      //   createdAt: new Date(),
      // });
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);
      const docRef = doc(firestore, "contracts", contractRef.id)
      await updateDoc(docRef, {
        id: contractRef.id,
        fileUrl:fileUrl // Only update the summary field
      });
      setContractId(contractRef.id);
      // Call Firebase Function to Extract Contract Details
      const extractContractDetails = httpsCallable(functions, "extractContractDetails");
      const response = await extractContractDetails({ filePath: `organizations/${orgId}/contracts/${contractRef.id}/${file.name}`, fileName: file.name, conId: contractRef.id});

      // Convert summary keys to proper English for display and store in editedFields
      const formattedSummary = Object.fromEntries(
        Object.entries(response.data.summary).map(([key, value]) => [toProperEnglish(key), value])
      );
      setSummary(response.data.summary); // Keep original for reference
      setEditedFields(formattedSummary);

      // Update contract with extracted summary
      await updateDoc(doc(firestore, "contracts", contractRef.id), {
        summary: response.data.summary,
        status: "processed",
      });

      alert("Contract uploaded and processed successfully!");
    } catch (error) {
      console.error("Error processing contract:", error);
      alert("Failed to upload and process the contract.");
    }

    setUploading(false);
  };

  const handleFieldChange = (field, value) => {
    setEditedFields((prev) => ({ ...prev, [field]: value }));
  };

  const addCustomField = () => {
    const newFieldName = prompt("Enter the name of the new field:");
    if (newFieldName) {
      const camelCaseKey = toCamelCase(newFieldName);
      setEditedFields((prev) => ({ ...prev, [newFieldName]: "" }));
    }
  };

  const handleSave = async () => {
    if (!contractId || !orgId) return;
    try {
      // Convert editedFields back to camelCase for saving
      const camelCaseSummary = Object.fromEntries(
        Object.entries(editedFields).map(([key, value]) => [toCamelCase(key), value])
      );

      // Query the existing document by the id field (UUID) and update the summary
      // const q = query(collection(firestore, "contracts"), where("id", "==", contractId));
      // const querySnapshot = await getDocs(q);
      // console.log('attempt query')
      const docRef = doc(firestore, "contracts", contractId);
      const gDoc = await getDoc(docRef)
      if (gDoc.exists()) {
        // console.log('got the docs attempting update')
        // const docRef = doc(firestore, "contracts", contractId);
        await updateDoc(docRef, {
          orgId,
          summary: camelCaseSummary, // Only update the summary field
        });
        alert("Contract details saved successfully!");
        window.location.reload()
      } else {
        throw new Error("Contract not found with the provided ID.");
      }
    } catch (error) {
      console.error("Error saving contract details:", error);
      alert("Failed to save contract details. " + error.message);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg mt-8">
      <h2 className="text-2xl font-semibold mb-4 text-center text-blue-600 dark:text-blue-400">Upload Contract</h2>

      {orgId ? (
        <>
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors 
              ${dragging ? "border-blue-500 bg-blue-100 dark:bg-gray-700" : "border-gray-300 dark:border-gray-600"}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input type="file" onChange={handleFileChange} className="hidden" id="fileUpload" />
            <label htmlFor="fileUpload" className="cursor-pointer text-gray-600 dark:text-gray-300 block">
              {file ? file.name : "Drag & Drop a file here or Click to select"}
            </label>
          </div>

          <button
            className={`mt-4 w-full px-6 py-3 text-white rounded-lg ${uploading ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"}`}
            onClick={handleUpload}
            disabled={uploading}
          >
            {uploading ? "Uploading..." : "Upload & Process"}
          </button>

          {summary && (
            <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <h3 className="text-lg font-bold">Review & Edit Contract Details</h3>
              {Object.entries(editedFields).map(([key, value]) => (
                <div key={key} className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{key}</label>
                  <input
                    type="text"
                    className="w-full p-2 border dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white"
                    value={value || ""}
                    onChange={(e) => handleFieldChange(key, e.target.value)}
                  />
                </div>
              ))}
              <button onClick={addCustomField} className="mt-2 bg-gray-500 text-white px-3 py-2 rounded hover:bg-gray-600">
                + Add Field
              </button>
              <button onClick={handleSave} className="mt-4 w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700">
                Save Contract
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="text-center text-red-500">Loading organization details...</p>
      )}
    </div>
  );
};

export default UploadContract;